/**
 * Task-003 (2026-audio-module-audit, AC-4) — the loudness-ladder SINGLE SOURCE
 * OF TRUTH. Before this module, gain decisions were scattered across six
 * places (66→73 per-profile `gain:` values in `AudioProfiles.ts`, the bus
 * bases + boot-ambience cue levels in `AudioEngine.ts`, hand-duplicated copies
 * of those same cue levels + a native-music preload level in
 * `platform/nativeAudio.ts`, duck depths, master dynamics, and doc-only
 * mastering numbers) with no dB-denominated model and no test asserting any of
 * it (`tech-analysis.md` §3.1).
 *
 * This module fixes that for the LOUDNESS half (routing/ducking stays exactly
 * as it was — see the "what did NOT change" note at the bottom):
 *
 * 1. **Bus-level gain constants** (`MUSIC_BASE`/`SFX_BASE`/`AMBIENCE_BASE`/
 *    `GRIT_GAIN`/`STING_GAIN`, originally also `TITLE_GAIN`) — relocated
 *    verbatim (values UNCHANGED at the time) out of `AudioEngine.ts`'s local
 *    consts and `nativeAudio.ts`'s hand-duplicated copies. Both now import
 *    from here — the hand-sync is over. D-244's "one-line ear-check tune"
 *    pattern stands: every constant below is still a single, named,
 *    owner-tunable number. (Task-001, 2026-audio-polish: `GRIT_GAIN` trimmed
 *    −15 % — AC-1; `TITLE_GAIN` retired with `title_loop` itself — AC-2.)
 * 2. **A category ladder aligned to the D-233 tier model**
 *    (`docs/design/AUDIO_BIBLE.md` §3.2's fatigue-system priority tiers) —
 *    `SFX_CRITICAL` (tier 0) … `SFX_REPEAT` (tier 3), plus `MUSIC_BED` and
 *    `AMBIENCE_BED` for the two non-SFX buses. Each category gets a **relative
 *    dB target band** vs. the `SFX_REF_DBFS` anchor (tier-0's own measured
 *    average) — see "How the bands were derived" below.
 * 3. **A per-profile category assignment** for every `AudioProfiles.ts` entry
 *    (ground truth is 73 profiles today — `tech-analysis.md`'s "66" is stale;
 *    flagged as a doc-drift finding for the Task-004 ledger, not fixed here).
 *    `REPEAT_TIER` and `SFX_DUCK_TRIGGERS` — previously two independently
 *    hand-kept `Set`s in `AudioEngine.ts` — are now DERIVED from this one
 *    table, so routing and loudness stop being parallel lists. Set membership
 *    is preserved EXACTLY (18 / 13 members respectively) — this module is a
 *    relocation + codification of today's behavior, not a redesign.
 *
 * ## How the bands were derived (A-7 / "de-facto mix first")
 * `tech-analysis.md` §3.2 proposed illustrative offsets (tier-0 = 0 dB
 * reference; tier-1 ≈ −3; tier-2 ≈ −8; tier-3 ≈ −14…−18; music ≈ −12 vs the
 * SFX reference; ambience ≈ −6 under music) as a PRE-MEASUREMENT starting
 * point. Computing the real synthesis RMS (× the default-volume bus math) for
 * all 73 profiles showed the actual shipped mix is close on tier-0/1/3, but:
 *   - tier-1 (IMPORTANT) and tier-2 (FEEDBACK) are barely separated in
 *     practice (measured averages −29.4 vs −29.6 dBFS — 0.2 dB apart, not the
 *     proposed ~5 dB gap). The D-233 tiers are semantically distinct via
 *     DUCK-TRIGGER behavior and event importance, not a strict loudness
 *     partition — bands are widened to match this reality rather than forcing
 *     dozens of unverified (un-ear-checked) retunes. Flagged for the owner's
 *     perceptual pass (Task-005) if a clearer tier gap is wanted.
 *   - `MUSIC_BED` measures at SFX-ref **+3.5 dB** (louder than the SFX
 *     reference), not the proposed −12 dB — because `MUSIC_BASE` is pinned at
 *     0.4 pending the very owner ear-check the pre-existing `AudioEngine.ts`
 *     comment already flags ("still a bed but ~3 dB above the original design
 *     point"). This module does not move `MUSIC_BASE` (out of scope — see
 *     below); the band reflects today's actual, admittedly-pending-review
 *     level rather than the pre-measurement guess.
 * Per the spec's own escape valve ("if today's de-facto mix makes a band
 * impossible without an audible change, prefer widening/adjusting the band to
 * match reality and flag it — behavior-preserving first"), bands below are
 * WIDE ENOUGH to contain today's shipped mix except for a small number of
 * genuinely isolated, machine-evident outliers, which got a small, bounded
 * gain retune instead (see "Outlier retunes" below and the `// Task-003
 * outlier retune` comments beside each). `SFX_REF_DBFS` itself is a fixed,
 * named anchor (not recomputed live), so a future retune never silently
 * shifts the whole ladder.
 *
 * ## Outlier retunes (both in `AudioProfiles.ts`, gain-only, one line each)
 * - `mainframe_hit_heavy` 0.30 → 0.44: noise+tight-lowpass(600 Hz) synthesis
 *   crushes RMS far more than its `gain` constant suggests — it measured
 *   12 dB quieter than its `SFX_CRITICAL` peers (−35.75 dBFS vs. the −23.7…
 *   −28.6 cluster the rest of tier-0 sits in), i.e. quieter than several
 *   `SFX_FEEDBACK`/`SFX_IMPORTANT` cues despite being "mainframe took a heavy
 *   hit". The bump lands it at −32.4 dBFS, inside `SFX_CRITICAL`'s band.
 * - `signal_overload_hum` 0.20 → 0.15: a sustained square wave (inherently
 *   near-unity RMS-to-peak, unlike noise/sine/chord) at a slow linear decay
 *   measured LOUDER than most `SFX_CRITICAL` members despite being the
 *   secondary "hum" layer of an already-critical alarm. The trim lands it at
 *   −23.1 dBFS, inside `SFX_IMPORTANT`'s band.
 * Both are named-constant edits only (D-244 pattern) — no envelope/filter/
 * duck/threshold change. The headless witness (`tests/systems/
 * AudioLoudness.test.ts`) proves these are necessary (fails without them) and
 * sufficient (passes with them).
 *
 * ## What did NOT change
 * Duck depths (`triggerDuck`/`triggerSfxDuck`/the pause interlude duck),
 * fatigue thresholds (fire-budget, density strides, voice cap), and the
 * D-233/D-240/D-215 behavior are all untouched — their witnesses stay green.
 * `MUSIC_BASE` stays 0.4; the pending owner ear-check the code already flags
 * is explicitly Task-005's item (the structured owner playtest, spec AC-8),
 * not re-litigated here.
 *
 * Sampled assets (the `theme.m4a` music master, the three boot/title ambience
 * cues) have no synthesis buffer to measure — they enter the ladder via their
 * DOCUMENTED mastering numbers instead (D-244, `docs/decisions.md:3810`: boot
 * grit/floppy ≈ −21 LUFS bed, the alarm sting ≈ −18 LUFS, −3 dBFS true-peak;
 * the theme master ≈ −11.54 dBFS integrated RMS, `AudioEngine.ts`'s
 * `MUSIC_BASE` comment) × the runtime gains below — LUFS is treated as an
 * engineering-approximation stand-in for dBFS-RMS here (no new DSP/decode
 * tooling, per Assumption A-7; both are conservative, doc-sourced ballpark
 * figures, not a mastering-grade loudness measurement).
 */

// ── Bus-level gain constants (relocated, values unchanged) ──────────────────

/**
 * Loudness bus defaults (PT-19 / D-142). The music master sits deliberately
 * *under* SFX so the theme is a bed, not a competitor for the player's ear.
 * Measured against the shipped master (`AI-WARs_Theme_v3.wav`: peak +0.20 dBFS,
 * integrated RMS -11.54 dBFS — a hot master, ~3.3 dB louder than the original
 * v1; the slight >0 dBFS true-peak is harmless here since the bed gain pulls it
 * ~12 dB down before the destination): at the default music slider (0.6) the
 * effective gain is 0.6 * 0.40 = 0.24 (-12.4 dB), i.e. ~-24 dBFS playback RMS —
 * still a bed but ~3 dB above the original design point, raisable to ~-19.5 dBFS
 * at slider max. MUSIC_BASE is left at 0.4 pending an owner ear-check (spec
 * AC-8 / Task-005's structured playtest — see the module doc above); drop to
 * ~0.28 to restore the original ~-27 dBFS default bed.
 */
export const MUSIC_BASE = 0.4;
export const SFX_BASE = 0.9;

/**
 * BL-143 / D-244 (S147) — the boot & start-screen ambience lane's bus base.
 * Tracks the MUSIC mute + volume (this is music-adjacent atmosphere, not
 * SFX) — `effectiveAmbienceGain = musicMuted ? 0 : musicVolume * AMBIENCE_BASE`.
 */
export const AMBIENCE_BASE = 0.9;

/**
 * Loading grit (POST/boot terminal): a steady bed; in on boot, out at
 * hand-off. Owner follow-up: these were "Störgeräusche" — pulled down so
 * they sit as subtle atmosphere, not foreground noise.
 *
 * Task-001 (2026-audio-polish, AC-1) asked for a "−15 %" trim; that shipped as
 * `0.35 × 0.85` (−1.41 dB) and the owner heard NO change on device (Build 67
 * ear-check) — correctly, since ~1.4 dB is below the loudness JND for a short
 * boot cue. "−15 %" was a guessed number for a PERCEPTUAL goal ("noticeably
 * quieter"), so this is now `0.35 × 0.6` (−4.4 dB from the original bed, −3.1 dB
 * below the old trim) — a clearly-audible reduction and a by-ear STARTING point
 * to confirm/dial on the next device test, not a math target. This is at the
 * FLOOR of the `AMBIENCE_BED` band: the recomputed witness lands at ≈ −40.0 dBFS,
 * ~1 dB inside [−41, −27] (`tests/systems/AudioLoudness.test.ts`). Quieter than
 * this needs a loudness-governance change (lower the ambience floor) or muting
 * the cue outright — an owner call, not a silent nudge. Linear gain, matching
 * this ladder's model (see the module doc); the native path picks this up by
 * import (no duplicated literal).
 */
export const GRIT_GAIN = 0.35 * 0.6;
/** Threat sting: one-shot on the first red threat line, runs to its end (may
 *  bleed into the title). Still the hottest of the three so it reads as an alarm,
 *  but bedded well under the old level (owner: keep it subtle). Untouched by
 *  Task-001's grit-only trim (AC-1: "no other cue gain moved"). */
export const STING_GAIN = 0.5;

/**
 * Task-001 (2026-ingame-music-rework, AC-3/AC-6) — the `"run"` procedural
 * composer's voice gains, the SSOT `MusicPatterns.ts`'s `VOICE_DEFS` imports
 * (each feeds straight into a `ProceduralProfile.gain` field, exactly like
 * every SFX profile's own `gain`). Kept here — not in `MusicPatterns.ts` —
 * because these are LOUDNESS numbers (what the `MUSIC_BED` conformance
 * witness tunes), while `MusicPatterns.ts` owns the MUSICAL data (scale,
 * tempo, register, density). One owner ear-check revision round (spec AC-8)
 * edits these named constants only — no architecture change.
 *
 * `pad` (L0) is the near-continuous voice the `MUSIC_BED` RMS is effectively
 * anchored on: at any energy ≥ its threshold (0) it is the only voice
 * sounding close to 100% of the render window (a long, linear-decay chord
 * retriggered every `PAD_BAR_PERIOD` bars, `MusicPatterns.ts`), so its gain
 * had to be pushed well above a typical one-shot chord SFX's (`wave_start`'s
 * 0.25, say) to reach the `MUSIC_BED` band at all — a sustained voice with a
 * decaying envelope needs far more per-hit gain than a rare accent to
 * deliver the SAME average RMS. The short/sparse voices (bass pulse,
 * percussion, arp, tension) sit much lower per-hit by comparison — at
 * today's density they barely move the aggregate RMS (measured <0.1 dB
 * swing across the tested energies), so their levels here are tuned for
 * PERCEIVED presence/contrast against the pad bed, not the ladder target;
 * the ladder is effectively pad's alone to hold. Calibrated against the
 * `MUSIC_BED` band (`musicBedRangeDb()`) via the pure-math bar-render
 * witness (`tests/systems/AudioLoudness.test.ts`) at energies
 * {0.2, 0.5, 0.9} — all three measured within ~0.03 dB of the band CENTER
 * (−24.0 dBFS), i.e. comfortably inside the ±3 dB tolerance on both sides.
 */
export const MUSIC_PAD_GAIN = 0.95;
export const MUSIC_BASS_GAIN = 0.12;
export const MUSIC_PERC_KICK_GAIN = 0.14;
export const MUSIC_PERC_HAT_GAIN = 0.03;
export const MUSIC_ARP_GAIN = 0.08;
export const MUSIC_TENSION_GAIN = 0.07;

/**
 * Task-003 (2026-audio-polish, AC-6) — the composer's `lead` voice (the
 * melody, `MusicPatterns.ts`'s `VOICE_DEFS.lead`): a long (≈800 ms), sparse
 * (1–2 notes per eligible bar, presence-gated) sine, NOT a near-continuous
 * voice like `pad`.
 *
 * REVISION (2026-07-22, Build 67 ear-check): the owner heard the run music as
 * "absolut monoton" — the melody wasn't landing. It shipped at 0.09, mixed
 * "just above the arp" as an ORNAMENT, and (worse) it was energy-gated OFF
 * below 0.25, so calmer stretches carried NO melody at all. Raised to 0.16
 * (+5 dB/note) and the gate lowered (`LAYER_ENERGY.lead`, `MusicPatterns.ts`)
 * so the melody is present from early combat, not just intense moments — it
 * now reads as a lead, still under the pad bed that anchors `MUSIC_BED`. This
 * is the MAX the `MUSIC_BED` ±3 dB band allows at full-energy without breaking
 * conformance (`tests/systems/AudioLoudness.test.ts`, energies {0.2,0.5,0.9});
 * louder needs a band-tolerance (governance) change. A by-ear starting point —
 * the owner confirms/dials "carries a melody, still unaufgeregt/kühl" on the
 * next device test.
 */
export const MUSIC_LEAD_GAIN = 0.16;

// ── The category ladder (D-233-aligned) ──────────────────────────────────────

/** D-233 tier 0 (CRITICAL) … tier 3 (REPEAT), plus the two non-SFX buses. */
export type LoudnessCategory =
  | "MUSIC_BED"
  | "AMBIENCE_BED"
  | "SFX_CRITICAL" // D-233 tier 0 — mainframe heavy/critical, boss spawn/defeat, game-over, victory
  | "SFX_IMPORTANT" // D-233 tier 1 — boss phase, wave start/complete, breach/tower-destroyed, big payload
  | "SFX_FEEDBACK" // D-233 tier 2 — place/upgrade/sell, cards, skills, firewalls, synergy
  | "SFX_REPEAT"; // D-233 tier 3 — tower fire, projectile hit, enemy death, economy (the "ping wall")

/** The 4 SFX tiers (excludes the two bus-level bed categories). */
export type SfxLoudnessCategory = Extract<LoudnessCategory, `SFX_${string}`>;

/** A relative dB target band: `targetOffsetDb` ± `toleranceDb` around a
 *  reference. See the module doc for how these were calibrated. */
export interface DbBand {
  readonly targetOffsetDb: number;
  readonly toleranceDb: number;
}

/**
 * The "0 dB" anchor every SFX band is expressed relative to — the measured
 * average effective output (synthesis RMS × `SFX_BASE` × the default SFX
 * slider) of today's `SFX_CRITICAL` cluster, rounded to a fixed named
 * constant (NOT recomputed live, so a future retune never silently drifts the
 * whole ladder).
 */
export const SFX_REF_DBFS = -27.5;

/** Per-category target band, dB relative to {@link SFX_REF_DBFS}. */
export const SFX_CATEGORY_BANDS: Readonly<Record<SfxLoudnessCategory, DbBand>> = {
  SFX_CRITICAL: { targetOffsetDb: 0, toleranceDb: 6 },
  SFX_IMPORTANT: { targetOffsetDb: -3, toleranceDb: 9 },
  SFX_FEEDBACK: { targetOffsetDb: -5, toleranceDb: 13 },
  SFX_REPEAT: { targetOffsetDb: -14, toleranceDb: 11.5 },
};

/** `MUSIC_BED`'s target band, dB relative to {@link SFX_REF_DBFS}. */
const MUSIC_BED_BAND: DbBand = { targetOffsetDb: 3.5, toleranceDb: 3 };

/** `AMBIENCE_BED`'s target band, dB relative to the RESOLVED `MUSIC_BED`
 *  center (the boot/title lane sits "under the music", per its own bus math —
 *  `effectiveAmbienceGain` tracks the music mute + volume, not the SFX ref). */
const AMBIENCE_BED_BAND: DbBand = { targetOffsetDb: -10, toleranceDb: 7 };

function rangeFromCenter(
  centerDb: number,
  band: DbBand,
): readonly [lo: number, hi: number] {
  const center = centerDb + band.targetOffsetDb;
  return [center - band.toleranceDb, center + band.toleranceDb];
}

/** `[lo, hi]` dBFS acceptance band for one of the 4 SFX tiers. */
export function sfxBandRangeDb(
  category: SfxLoudnessCategory,
): readonly [lo: number, hi: number] {
  return rangeFromCenter(SFX_REF_DBFS, SFX_CATEGORY_BANDS[category]);
}

/** `MUSIC_BED`'s resolved target center, dBFS. */
export function musicBedCenterDb(): number {
  return SFX_REF_DBFS + MUSIC_BED_BAND.targetOffsetDb;
}

/** `[lo, hi]` dBFS acceptance band for `MUSIC_BED`. */
export function musicBedRangeDb(): readonly [lo: number, hi: number] {
  return rangeFromCenter(SFX_REF_DBFS, MUSIC_BED_BAND);
}

/** `[lo, hi]` dBFS acceptance band for `AMBIENCE_BED`. */
export function ambienceBedRangeDb(): readonly [lo: number, hi: number] {
  return rangeFromCenter(musicBedCenterDb(), AMBIENCE_BED_BAND);
}

// ── Per-profile category assignment (all 73 AudioProfiles.ts entries) ───────

/** One profile's loudness-ladder assignment. `duckTrigger: true` marks the
 *  73 - however-many members of today's `SFX_DUCK_TRIGGERS` (D-233 "tiers 0–1
 *  duck the repeat bed so they read clearly") — assigned per-profile, not
 *  purely derived from `category`, because not every CRITICAL/IMPORTANT
 *  member ducks: `heartbeat` (categorized `SFX_FEEDBACK` below — see its
 *  entry) repeats every 500–800 ms, and self-ducking at that cadence would be
 *  self-defeating/thrashy, so it was never wired as a trigger; secondary
 *  companion layers of an already-duck-worthy moment (`tower_destroyed_crash`/
 *  `path_distortion` alongside `tower_destroyed`) don't each re-trigger the
 *  same duck either. */
interface SfxAssignment {
  readonly category: SfxLoudnessCategory;
  readonly duckTrigger?: true;
}

export const SFX_LOUDNESS_ASSIGNMENTS: Readonly<Record<string, SfxAssignment>> = {
  // ── D-233 tier 0 — SFX_CRITICAL: full weight; ducks the repeat bed −6 dB;
  // never voice-stolen (mainframe heavy/critical · boss spawn/defeat ·
  // game-over · victory; `boss_stinger`/`signal_overload` are the general
  // "full-attention alarm" cues, same weight class). ──────────────────────
  mainframe_hit_critical: { category: "SFX_CRITICAL", duckTrigger: true },
  // Task-003 outlier retune (AudioProfiles.ts gain 0.30→0.44) — see module doc.
  mainframe_hit_heavy: { category: "SFX_CRITICAL", duckTrigger: true },
  boss_spawn: { category: "SFX_CRITICAL", duckTrigger: true },
  boss_defeat: { category: "SFX_CRITICAL", duckTrigger: true },
  boss_stinger: { category: "SFX_CRITICAL", duckTrigger: true },
  game_over: { category: "SFX_CRITICAL", duckTrigger: true },
  victory_fanfare: { category: "SFX_CRITICAL", duckTrigger: true },
  signal_overload: { category: "SFX_CRITICAL", duckTrigger: true },

  // ── D-233 tier 1 — SFX_IMPORTANT: clear weight; ducks the repeat bed −3 dB
  // (boss phase · wave start/complete · breach/tower-destroyed · big payload ·
  // low-lives heartbeat). ──────────────────────────────────────────────────
  boss_phase: { category: "SFX_IMPORTANT", duckTrigger: true },
  wave_start: { category: "SFX_IMPORTANT", duckTrigger: true },
  wave_complete: { category: "SFX_IMPORTANT", duckTrigger: true },
  tower_destroyed: { category: "SFX_IMPORTANT", duckTrigger: true },
  payload_detonate: { category: "SFX_IMPORTANT", duckTrigger: true },
  // Task-003 outlier retune (AudioProfiles.ts gain 0.20→0.15) — see module doc.
  // Companion layer of `signal_overload` (tier 0) — not itself a duck trigger
  // (matches today's shipped `SFX_DUCK_TRIGGERS`, which never included it).
  signal_overload_hum: { category: "SFX_IMPORTANT" },

  // ── D-233 tier 2 — SFX_FEEDBACK: normal weight, dry path, no duck (place ·
  // upgrade · sell · card · skill · firewall · synergy · most UI · payload
  // arm/fire/companion layers · the severity cascade's lower rungs). ───────
  tower_place: { category: "SFX_FEEDBACK" },
  tower_sell: { category: "SFX_FEEDBACK" },
  tower_upgrade: { category: "SFX_FEEDBACK" },
  enemy_leak: { category: "SFX_FEEDBACK" },
  card_equip: { category: "SFX_FEEDBACK" },
  card_equip_common: { category: "SFX_FEEDBACK" },
  card_equip_rare: { category: "SFX_FEEDBACK" },
  card_equip_epic: { category: "SFX_FEEDBACK" },
  card_equip_legendary: { category: "SFX_FEEDBACK" },
  ui_click: { category: "SFX_FEEDBACK" },
  countdown_beep: { category: "SFX_FEEDBACK" },
  wave_boot: { category: "SFX_FEEDBACK" },
  // Below `mainframe_hit_{heavy,critical}` in the severity cascade — today's
  // shipped `SFX_DUCK_TRIGGERS` never included the small/medium rungs either.
  mainframe_hit_small: { category: "SFX_FEEDBACK" },
  mainframe_hit_medium: { category: "SFX_FEEDBACK" },
  // De-facto placement (see the per-profile assignment note above): the
  // AUDIO_BIBLE prose calls the near-loss heartbeat tier-1 IMPORTANT, but its
  // shipped gain (0.15) and non-duck-trigger membership already match tier-2
  // — codifying today's actual behavior takes precedence over the doc's
  // pre-implementation intent (A-7). Flagged for Task-005's perceptual pass.
  heartbeat: { category: "SFX_FEEDBACK" },
  tower_destroyed_crash: { category: "SFX_FEEDBACK" },
  path_stream: { category: "SFX_FEEDBACK" },
  path_distortion: { category: "SFX_FEEDBACK" },
  payload_kernelPanic: { category: "SFX_FEEDBACK" },
  payload_reduceMaxHp: { category: "SFX_FEEDBACK" },
  ui_confirm: { category: "SFX_FEEDBACK" },
  ui_error: { category: "SFX_FEEDBACK" },
  ui_open: { category: "SFX_FEEDBACK" },
  ui_close: { category: "SFX_FEEDBACK" },
  skill_activate: { category: "SFX_FEEDBACK" },
  firewall_place: { category: "SFX_FEEDBACK" },
  firewall_sell: { category: "SFX_FEEDBACK" },
  firewall_destroy: { category: "SFX_FEEDBACK" },
  payload_arm: { category: "SFX_FEEDBACK" },
  payload_fire: { category: "SFX_FEEDBACK" },
  achievement_unlock: { category: "SFX_FEEDBACK" },
  synergy_trigger: { category: "SFX_FEEDBACK" },
  pause_in: { category: "SFX_FEEDBACK" },
  pause_out: { category: "SFX_FEEDBACK" },
  time_warp: { category: "SFX_FEEDBACK" },
  payload_gcForce: { category: "SFX_FEEDBACK" },
  payload_logicBomb_arm: { category: "SFX_FEEDBACK" },
  payload_logicBomb: { category: "SFX_FEEDBACK" },
  payload_backdoor: { category: "SFX_FEEDBACK" },
  payload_forkBomb: { category: "SFX_FEEDBACK" },
  payload_pidHijack: { category: "SFX_FEEDBACK" },

  // ── D-233 tier 3 — SFX_REPEAT: routed to `sfxRepeatBus`, budgeted
  // (fire-budget/density throttle), ducked BY tiers 0–1 (never itself a
  // trigger) — tower fire · projectile hit · enemy death/spawn · economy ·
  // the two deliberately-extreme "quietest of all" UI cues (ui_hover) and the
  // reward sting routed here for its ducking semantics (combo_milestone). ──
  tower_shoot: { category: "SFX_REPEAT" },
  tower_fire_fire: { category: "SFX_REPEAT" },
  tower_fire_water: { category: "SFX_REPEAT" },
  tower_fire_lightning: { category: "SFX_REPEAT" },
  tower_fire_signal: { category: "SFX_REPEAT" },
  tower_fire_virus: { category: "SFX_REPEAT" },
  tower_fire_dark: { category: "SFX_REPEAT" },
  hit_fire: { category: "SFX_REPEAT" },
  hit_water: { category: "SFX_REPEAT" },
  hit_lightning: { category: "SFX_REPEAT" },
  hit_signal: { category: "SFX_REPEAT" },
  hit_virus: { category: "SFX_REPEAT" },
  hit_dark: { category: "SFX_REPEAT" },
  enemy_death: { category: "SFX_REPEAT" },
  enemy_spawn: { category: "SFX_REPEAT" },
  economy_tick: { category: "SFX_REPEAT" },
  // Routed tier-3 for its DUCKING semantics (a milestone must never fight a
  // marquee cue — see its `AudioProfiles.ts` comment) even though it reads as
  // a reward; its measured level (−30.5 dBFS) is deliberately the loudest
  // member of this tier, comfortably inside the widened `SFX_REPEAT` band.
  combo_milestone: { category: "SFX_REPEAT" },
  // S147: deliberately the quietest sound in the game (gain 0.022) — an
  // existing witness (`AudioEngine.test.ts`) asserts it's quieter than
  // `ui_click`. Intentional, not an outlier.
  ui_hover: { category: "SFX_REPEAT" },
};

/** D-233 tier 3 — the repeat-tier bed (`sfxRepeatBus` routing). Derived from
 *  {@link SFX_LOUDNESS_ASSIGNMENTS} so this and the loudness category stop
 *  being two hand-kept lists; membership is byte-identical to the set this
 *  replaces in `AudioEngine.ts` (18 members). */
export const REPEAT_TIER: ReadonlySet<string> = new Set(
  Object.entries(SFX_LOUDNESS_ASSIGNMENTS)
    .filter(([, a]) => a.category === "SFX_REPEAT")
    .map(([name]) => name),
);

/** D-233 tiers 0–1 — the SFX names that duck the repeat-tier bed on play.
 *  Derived from {@link SFX_LOUDNESS_ASSIGNMENTS}'s `duckTrigger` flags;
 *  membership is byte-identical to the set this replaces in `AudioEngine.ts`
 *  (13 members). */
export const SFX_DUCK_TRIGGERS: ReadonlySet<string> = new Set(
  Object.entries(SFX_LOUDNESS_ASSIGNMENTS)
    .filter(([, a]) => a.duckTrigger === true)
    .map(([name]) => name),
);
